import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Phone,
  ShieldCheck,
  Pizza as PizzaIcon,
  Beef,
  Sandwich,
  Flame,
  UtensilsCrossed,
  Leaf,
  Coffee,
  IceCream,
  Tag,
  Clock3,
  CheckCircle2,
} from "lucide-react";
import { MENU } from "@/lib/testIds";
import { MenuOptionDialog } from "@/components/MenuOptionDialog";
import { AddToCartButton } from "@/components/AddToCartButton";
import { parseEUR } from "@/lib/cartStore";
import { Seo } from "@/lib/seo";
import {
  PIZZA,
  PIZZA_SIGNATURES,
  CHICKEN_CHOICES,
  BURGERS,
  NAAN,
  MERGUEZ,
  ENTREES,
  SIDES,
  DRINKS,
  DESSERTS,
  OFFERS,
  RESTAURANT,
  COPY,
} from "@/data/catalog";

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const NonContractual = () => (
  <div className="absolute bottom-1 right-2 text-[10px] text-white/45 italic">
    {COPY.nonContractual}
  </div>
);

const SectionTitle = ({ kicker, title, icon: Icon }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#C9A875]/10 border border-[#C9A875]/30 text-[#C9A875] shrink-0">
      <Icon size={18} strokeWidth={2.4} />
    </span>
    <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#C9A875] font-street">
        {kicker}
      </div>
      <h2 className="font-street text-2xl sm:text-3xl tracking-wide leading-none mt-1">
        {title}
      </h2>
    </div>
  </div>
);

/**
 * En-tête d'une grande catégorie (Menus & Offres, Starters, Boissons & Desserts).
 * Pas d'icône — ces blocs sont des regroupements et non des produits.
 */
const GroupHeader = ({ id, kicker, title, intro }) => (
  <section id={id} className="scroll-mt-24 pt-12 pb-1">
    <div className="border-t border-[#262626] pt-7">
      <div className="text-[11px] uppercase tracking-[0.32em] text-[#C9A875] font-street">
        {kicker}
      </div>
      <h2 className="font-street mt-2 text-3xl sm:text-4xl tracking-wide leading-none">
        {title}
      </h2>
      {intro && (
        <p className="mt-3 text-sm text-[#A1A1AA] leading-snug max-w-2xl">
          {intro}
        </p>
      )}
    </div>
  </section>
);

const ItemRow = ({ id, name, desc, price, option, addable, testIdPrefix }) => {
  const unitPrice = parseEUR(price);
  return (
    <li className="py-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-white">{name}</span>
        <span className="font-display text-lg leading-none text-[#D4B27A] underline decoration-dotted underline-offset-4">
          {price}
        </span>
      </div>
      {desc && (
        <div className="mt-1 text-[12px] text-[#D4D4D8] leading-snug">{desc}</div>
      )}
      {option && (
        <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-[#262626] bg-[#0A0A0A] px-2.5 py-1 text-[11px]">
          <span className="text-[#C9A875] font-bold">+</span>
          <span className="text-white">{option.label}</span>
          <span className="font-display text-[#D4B27A]">{option.price}</span>
        </div>
      )}
      {addable && unitPrice > 0 && (
        <div className="mt-2.5">
          <AddToCartButton
            item={{ kind: "item", name, details: desc ? [desc] : [], unitPrice }}
            testId={`${testIdPrefix || "menu-add"}-${id}`}
          />
        </div>
      )}
    </li>
  );
};

/* -------------------------------------------------------------------------- */
/*  PIZZA SIGNATURES — grille premium groupée par base, prix unique 10 €      */
/* -------------------------------------------------------------------------- */

const PizzaSignatureCard = ({ pizza }) => {
  const unitPrice = parseEUR(PIZZA_SIGNATURES.unitPrice);
  return (
    <article
      data-testid={`pizza-signature-${pizza.id}`}
      className="flex flex-col gap-2 rounded-xl border border-[#262626] bg-[#0F0F0F] p-3.5 hover:border-[#C9A875]/35 transition-colors"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="font-street text-base sm:text-lg tracking-wide leading-none">
          {pizza.name}
        </h4>
        <span className="font-display text-lg leading-none text-[#D4B27A]">
          {PIZZA_SIGNATURES.unitPrice}
        </span>
      </div>
      <p className="text-[12.5px] text-[#D4D4D8] leading-snug">
        {pizza.composition}
      </p>
      <div className="mt-1">
        <AddToCartButton
          item={{
            kind: "item",
            name: `Pizza ${pizza.name}`,
            details: [pizza.composition],
            unitPrice,
          }}
          testId={`pizza-signature-add-${pizza.id}`}
        />
      </div>
    </article>
  );
};

const PizzaSignaturesSection = () => {
  const [activeBase, setActiveBase] = useState(PIZZA_SIGNATURES.groups[0].id);
  const currentGroup =
    PIZZA_SIGNATURES.groups.find((g) => g.id === activeBase) ||
    PIZZA_SIGNATURES.groups[0];

  return (
    <section id={PIZZA_SIGNATURES.anchor} className="scroll-mt-24 py-10">
      <SectionTitle
        kicker="Nos recettes"
        title={PIZZA_SIGNATURES.title}
        icon={PizzaIcon}
      />

      {/* En-tête : prix unique + mention compose à la demande + CTA signatures */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <span className="inline-flex items-center gap-2 rounded-md border border-[#C9A875]/35 bg-[#C9A875]/[0.08] px-3 py-1.5 text-[12px] font-street uppercase tracking-[0.22em] text-[#C9A875]">
          Prix unique 10 €
        </span>
        <span className="text-[12px] text-white/65 italic">
          {PIZZA_SIGNATURES.composeHint}
        </span>
        <a
          href="#pizza"
          data-testid="pizza-signatures-cta"
          className="font-street inline-flex items-center gap-1.5 rounded-md border border-[#F5EFE6]/20 bg-[#F5EFE6]/[0.03] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#F5EFE6]/85 hover:border-[#F5EFE6]/40 hover:bg-[#F5EFE6]/[0.06] hover:text-[#F5EFE6] transition-colors"
        >
          Voir les pizzas signatures à 10 €
        </a>
      </div>

      {/* Sous-navigation : Base tomate / Base crème fraîche */}
      <div
        role="tablist"
        aria-label="Filtrer par base"
        data-testid="pizza-base-tabs"
        className="mb-5 inline-flex rounded-md border border-[#262626] bg-[#0A0A0A] p-1 gap-1"
      >
        {PIZZA_SIGNATURES.groups.map((g) => {
          const active = activeBase === g.id;
          return (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveBase(g.id)}
              data-testid={`pizza-base-tab-${g.id}`}
              className={`px-4 py-2 text-[12px] font-street uppercase tracking-[0.22em] rounded-md transition-colors ${
                active
                  ? "bg-[#C9A875] text-[#0A0A0A] shadow-[0_2px_0_rgba(0,0,0,0.3)]"
                  : "text-white/70 hover:text-white"
              }`}
            >
              {g.title}
            </button>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {currentGroup.items.map((p) => (
          <PizzaSignatureCard key={p.id} pizza={p} />
        ))}
      </div>

      {/* Mention halal */}
      <p className="mt-4 text-[11.5px] text-[#A1A1AA] italic leading-snug">
        {PIZZA_SIGNATURES.halalNote}
      </p>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*  Sandwich-like card (Burgers / Naan / Merguez)                              */
/* -------------------------------------------------------------------------- */

const NAAN_PAIN_NATURE = { id: "nature", label: "Naan nature", surcharge: 0 };
const NAAN_PAIN_CHEESE = {
  id: "cheese",
  label: "Pain Cheese Naan",
  surcharge: 1.5,
};
const NAAN_PAIN_OPTIONS = [NAAN_PAIN_NATURE, NAAN_PAIN_CHEESE];

const fmtEUR = (n) => `${n.toFixed(2).replace(".", ",")} €`;

/**
 * Ligne produit dédiée Naan — propose un toggle "Pain Naan nature / Pain Cheese
 * Naan +1,50 €" qui se répercute sur le prix affiché, le bouton "Ajouter seul"
 * et l'option Menu +3 €.
 */
/* ----- Choix Chicken Curry / Tandoori ----- */

const useChickenChoice = (enabled) => {
  const [choice, setChoice] = useState(enabled ? CHICKEN_CHOICES[0].id : null);
  const selected = enabled
    ? CHICKEN_CHOICES.find((c) => c.id === choice) || CHICKEN_CHOICES[0]
    : null;
  return { choice, setChoice, selected };
};

const ChickenChoiceSelector = ({ itemId, value, onChange }) => (
  <div className="mt-3" data-testid={`chicken-choice-${itemId}`}>
    <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-street mb-1.5">
      Chicken
    </div>
    <div
      role="radiogroup"
      aria-label="Choix du poulet"
      className="inline-flex rounded-md border border-[#262626] bg-[#0A0A0A] p-0.5"
    >
      {CHICKEN_CHOICES.map((c) => {
        const active = value === c.id;
        return (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(c.id)}
            data-testid={`chicken-choice-${itemId}-${c.id}`}
            className={`px-2.5 py-1 text-[11px] font-street tracking-wider uppercase rounded-md transition-colors ${
              active
                ? "bg-[#C9A875] text-[#0A0A0A]"
                : "text-white/65 hover:text-white"
            }`}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  </div>
);

const NaanItemRow = ({ item, groupId }) => {
  const [painId, setPainId] = useState("nature");
  const pain = NAAN_PAIN_OPTIONS.find((p) => p.id === painId) || NAAN_PAIN_NATURE;
  const chicken = useChickenChoice(!!item.chickenChoice);

  const basePrice = parseEUR(item.price);
  const finalPrice = basePrice + pain.surcharge;
  const finalPriceStr = fmtEUR(finalPrice);

  // Composition affichée : on remplace "Pain naan" par le pain choisi.
  const composition = (item.composition || []).map((c) =>
    c === "Pain naan"
      ? pain.id === "cheese"
        ? "Pain Cheese Naan"
        : "Pain naan nature"
      : c
  );

  const chickenDetail = chicken.selected
    ? `Chicken : ${chicken.selected.label}`
    : null;

  return (
    <li className="py-3.5 first:pt-0 last:pb-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-street text-base tracking-wide uppercase text-white">
          {item.name}
        </span>
        <span
          data-testid={`naan-price-${item.id}`}
          className="font-display text-lg leading-none text-[#D4B27A] underline decoration-dotted underline-offset-4"
        >
          {finalPriceStr}
        </span>
      </div>

      {composition.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {composition.map((c) => (
            <span
              key={c}
              className="inline-flex items-center rounded-full border border-[#262626] bg-[#0A0A0A] px-2.5 py-1 text-[11.5px] text-[#D4D4D8]"
            >
              {c}
            </span>
          ))}
        </div>
      )}

      {/* Choix Chicken (Curry / Tandoori) */}
      {item.chickenChoice && (
        <ChickenChoiceSelector
          itemId={item.id}
          value={chicken.choice}
          onChange={chicken.setChoice}
        />
      )}

      {/* Choix du pain */}
      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-street mb-1.5">
          Pain
        </div>
        <div
          role="radiogroup"
          aria-label="Choix du pain"
          className="inline-flex rounded-md border border-[#262626] bg-[#0A0A0A] p-0.5"
        >
          {NAAN_PAIN_OPTIONS.map((p) => {
            const active = painId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setPainId(p.id)}
                data-testid={`naan-pain-${item.id}-${p.id}`}
                className={`px-2.5 py-1 text-[11px] font-street tracking-wider uppercase rounded-md transition-colors ${
                  active
                    ? "bg-[#FF9500] text-black"
                    : "text-white/65 hover:text-white"
                }`}
              >
                {p.id === "cheese" ? "Cheese +1,50 €" : "Nature"}
              </button>
            );
          })}
        </div>
      </div>

      {/* Boutons d'ajout */}
      <div className="mt-3 flex flex-wrap gap-2">
        <AddToCartButton
          item={{
            kind: "item",
            name:
              pain.id === "cheese" ? `${item.name} — Cheese Naan` : item.name,
            details: [
              ...composition,
              ...(chickenDetail ? [chickenDetail] : []),
              ...(pain.id === "cheese"
                ? [`Pain Cheese Naan (+${fmtEUR(pain.surcharge)})`]
                : []),
            ],
            unitPrice: finalPrice,
          }}
          testId={`menu-add-${groupId}-${item.id}`}
          label="Ajouter seul"
        />
        <MenuOptionDialog
          product={{ ...item, name: item.name, price: item.price }}
          extras={
            pain.id === "cheese"
              ? { label: "Pain Cheese Naan", surcharge: pain.surcharge }
              : undefined
          }
          extraDetail={chickenDetail}
        />
      </div>
    </li>
  );
};

const BurgerItemRow = ({ item, groupId }) => {
  const chicken = useChickenChoice(!!item.chickenChoice);
  const chickenDetail = chicken.selected
    ? `Chicken : ${chicken.selected.label}`
    : null;
  const unitPrice = parseEUR(item.price);

  return (
    <li className="py-3.5 first:pt-0 last:pb-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-street text-base tracking-wide uppercase text-white">
          {item.name}
        </span>
        <span className="font-display text-lg leading-none text-[#D4B27A] underline decoration-dotted underline-offset-4">
          {item.price}
        </span>
      </div>
      {item.composition && item.composition.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {item.composition.map((c) => (
            <span
              key={c}
              className="inline-flex items-center rounded-full border border-[#262626] bg-[#0A0A0A] px-2.5 py-1 text-[11.5px] text-[#D4D4D8]"
            >
              {c}
            </span>
          ))}
        </div>
      )}
      {item.chickenChoice && (
        <ChickenChoiceSelector
          itemId={item.id}
          value={chicken.choice}
          onChange={chicken.setChoice}
        />
      )}
      <div className="mt-2.5 flex flex-wrap gap-2">
        <AddToCartButton
          item={{
            kind: "item",
            name: item.name,
            details: [
              ...(item.composition || []),
              ...(chickenDetail ? [chickenDetail] : []),
            ],
            unitPrice,
          }}
          testId={`menu-add-${groupId}-${item.id}`}
          label="Ajouter seul"
        />
        <MenuOptionDialog product={item} extraDetail={chickenDetail} />
      </div>
    </li>
  );
};

const ProductBlock = ({ anchor, kicker, group, icon: Icon, withMenuOption }) => (
  <section id={anchor} className="scroll-mt-24 py-10">
    <SectionTitle kicker={kicker} title={group.title} icon={Icon} />
    <article className="grid lg:grid-cols-12 gap-5 rounded-2xl border border-[#262626] bg-[#121212] overflow-hidden">
      <div className="lg:col-span-5 relative aspect-[4/3] lg:aspect-auto min-h-[14rem]">
        <img
          src={group.image}
          alt={group.title}
          loading="lazy" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/65 to-transparent" />
        <NonContractual />
      </div>
      <div className="lg:col-span-7 p-5 sm:p-6">
        <ul className="divide-y divide-[#1F1F1F]">
          {group.items.map((it) =>
            group.id === "naan" && withMenuOption ? (
              <NaanItemRow key={it.id} item={it} groupId={group.id} />
            ) : group.id === "burgers" && withMenuOption ? (
              <BurgerItemRow key={it.id} item={it} groupId={group.id} />
            ) : (
              <li key={it.id} className="py-3.5 first:pt-0 last:pb-0">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-street text-base tracking-wide uppercase text-white">
                    {it.name}
                  </span>
                  <span className="font-display text-lg leading-none text-[#D4B27A] underline decoration-dotted underline-offset-4">
                    {it.price}
                  </span>
                </div>
                {it.composition && it.composition.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {it.composition.map((c) => (
                      <span
                        key={c}
                        className="inline-flex items-center rounded-full border border-[#262626] bg-[#0A0A0A] px-2.5 py-1 text-[11.5px] text-[#D4D4D8]"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                )}
                {withMenuOption && (
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <AddToCartButton
                      item={{
                        kind: "item",
                        name: it.name,
                        details: it.composition || [],
                        unitPrice: parseEUR(it.price),
                      }}
                      testId={`menu-add-${group.id}-${it.id}`}
                      label="Ajouter seul"
                    />
                    <MenuOptionDialog product={it} />
                  </div>
                )}
              </li>
            )
          )}
        </ul>
        {group.options && group.options.length > 0 && !withMenuOption && (
          <div className="mt-4 flex flex-wrap gap-2">
            {group.options.map((o) => (
              <span
                key={o.id}
                className="inline-flex items-center gap-2 rounded-md border border-[#262626] bg-[#0A0A0A] px-2.5 py-1 text-[11px]"
              >
                <span className="text-white">{o.label}</span>
                <span className="font-display text-[#D4B27A]">{o.price}</span>
              </span>
            ))}
          </div>
        )}
        {withMenuOption && (
          <p className="mt-4 text-[11px] italic text-white/45 leading-snug">
            Option Menu +3 € : frites + boisson. Prix estimatif. Commande à
            confirmer par téléphone.
          </p>
        )}
      </div>
    </article>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Entrées (2 sous-groupes)                                                   */
/* -------------------------------------------------------------------------- */

const EntreesSection = () => (
  <section id={ENTREES.anchor} className="scroll-mt-24 py-6">
    <div className="grid gap-5 lg:grid-cols-2">
      {ENTREES.subgroups.map((sg) => (
        <article
          key={sg.id}
          className="rounded-2xl border border-[#262626] bg-[#121212] overflow-hidden flex flex-col"
        >
          <div className="relative h-44 sm:h-52">
            <img
              src={sg.image}
              alt={sg.title}
              loading="lazy" decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4">
              <div className="font-street text-xl tracking-wide leading-none text-[#F5EFE6]">
                {sg.title}
              </div>
            </div>
          </div>
          <div className="p-5 flex-1">
            <ul className="divide-y divide-[#1F1F1F]">
              {sg.items.map((it) => (
                <li key={it.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-white">{it.name}</span>
                    <span className="font-display text-lg leading-none text-[#D4B27A] underline decoration-dotted underline-offset-4">
                      {it.price}
                    </span>
                  </div>
                  {it.desc && (
                    <div className="mt-1 text-[12px] text-[#D4D4D8] leading-snug">
                      {it.desc}
                    </div>
                  )}
                  {it.variants && it.variants.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {it.variants.map((v) => (
                        <span
                          key={v}
                          className="inline-flex items-center rounded-full border border-[#C9A875]/20 bg-[#0A0A0A] px-2.5 py-1 text-[11px] text-white"
                        >
                          {v}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-2.5">
                    <AddToCartButton
                      item={{
                        kind: "item",
                        name: it.name,
                        details: it.desc ? [it.desc] : [],
                        unitPrice: parseEUR(it.price),
                      }}
                      testId={`menu-add-entree-${it.id}`}
                    />
                  </div>
                </li>
              ))}
            </ul>
            {sg.note && (
              <div className="mt-3 text-[11px] italic text-white/55">
                {sg.note}
              </div>
            )}
          </div>
        </article>
      ))}
    </div>
    <div className="mt-2 text-[10px] text-white/40 italic text-right pr-1">
      {COPY.nonContractual}
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Accompagnements                                                            */
/* -------------------------------------------------------------------------- */

const SidesSection = () => (
  <section id={SIDES.anchor} className="scroll-mt-24 py-10">
    <SectionTitle kicker="Compléments" title={SIDES.title} icon={Leaf} />
    <article className="grid lg:grid-cols-12 gap-5 rounded-2xl border border-[#262626] bg-[#121212] overflow-hidden">
      {/* Visuel décoratif (placeholder graphique en attendant photo dédiée) */}
      <div className="lg:col-span-5 relative aspect-[4/3] lg:aspect-auto min-h-[12rem] bg-gradient-to-br from-[#C9A875]/15 via-[#0F0F0F] to-[#0F0F0F] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              "radial-gradient(rgba(201,168,117,0.5) 1.5px, transparent 2px)",
            backgroundSize: "18px 18px",
          }}
        />
        <div className="relative flex flex-col items-center text-center px-4">
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#C9A875]/15 border border-[#C9A875]/40 text-[#C9A875] shadow-[0_8px_24px_rgba(0,0,0,0.5)]">
            <UtensilsCrossed size={28} strokeWidth={2.2} />
          </span>
          <div className="mt-4 font-street text-2xl sm:text-3xl tracking-wide text-white leading-none">
            Frites fraîches
          </div>
          <div className="mt-1 font-street text-2xl sm:text-3xl tracking-wide text-[#C9A875] leading-none">
            maison
          </div>
          <div className="mt-3 inline-flex flex-wrap gap-1.5 justify-center">
            {["Nature", "Cheddar", "Bacon halal"].map((t) => (
              <span
                key={t}
                className="inline-flex items-center rounded-full border border-[#C9A875]/25 bg-[#0A0A0A]/60 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] text-[#FFC447]/85 font-street"
              >
                {t}
              </span>
            ))}
          </div>
          <div className="mt-3 text-[10px] italic text-white/45">
            {COPY.nonContractual}
          </div>
        </div>
      </div>

      {/* Liste produits */}
      <div className="lg:col-span-7 p-5 sm:p-6">
        <ul className="divide-y divide-[#1F1F1F]">
          {SIDES.items.map((it) => (
            <ItemRow
              key={it.id}
              id={it.id}
              name={it.name}
              price={it.price}
              option={it.option}
              addable
              testIdPrefix="menu-add-side"
            />
          ))}
        </ul>
      </div>
    </article>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Boissons (groupes : sodas listés)                                          */
/* -------------------------------------------------------------------------- */

const DrinksSection = () => (
  <section id={DRINKS.anchor} className="scroll-mt-24 py-10">
    <SectionTitle kicker="À boire" title={DRINKS.title} icon={Coffee} />
    <article className="grid lg:grid-cols-12 gap-5 rounded-2xl border border-[#262626] bg-[#121212] overflow-hidden">
      <div className="lg:col-span-5 relative aspect-[4/3] lg:aspect-auto min-h-[14rem]">
        <img
          src={DRINKS.image}
          alt={DRINKS.title}
          loading="lazy" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/65 to-transparent" />
        {/* L'image fournie contient déjà la mention — pas de doublon */}
      </div>
      <div className="lg:col-span-7 p-5 sm:p-6 space-y-4">
        {DRINKS.groups.map((g) => (
          <div
            key={g.id}
            className="rounded-xl border border-[#262626] bg-[#0A0A0A] p-4"
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="font-street text-base tracking-wider uppercase text-white">
                {g.title}
              </div>
              <div className="font-display text-xl leading-none text-[#D4B27A]">
                {g.price}
              </div>
            </div>
            {g.items.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {g.items.map((it) => (
                  <li key={it.id}>
                    <AddToCartButton
                      item={{
                        kind: "item",
                        name: it.name,
                        details: [g.title],
                        unitPrice: parseEUR(g.price),
                      }}
                      testId={`menu-add-drink-${it.id}`}
                      label={it.name}
                    />
                  </li>
                ))}
              </ul>
            )}
            {g.items.length === 0 && parseEUR(g.price) > 0 && (
              <div className="mt-2">
                <AddToCartButton
                  item={{
                    kind: "item",
                    name: g.title,
                    details: [],
                    unitPrice: parseEUR(g.price),
                  }}
                  testId={`menu-add-drink-group-${g.id}`}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </article>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Desserts                                                                   */
/* -------------------------------------------------------------------------- */

const DessertsSection = () => (
  <section id={DESSERTS.anchor} className="scroll-mt-24 py-10">
    <SectionTitle kicker="Sucré" title={DESSERTS.title} icon={IceCream} />
    <article className="grid lg:grid-cols-12 gap-5 rounded-2xl border border-[#262626] bg-[#121212] overflow-hidden">
      <div className="lg:col-span-5 relative aspect-[4/3] lg:aspect-auto min-h-[14rem]">
        <img
          src={DESSERTS.image}
          alt={DESSERTS.title}
          loading="lazy" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/65 to-transparent" />
        {/* L'image fournie contient déjà la mention "Selon disponibilité" + photo non contractuelle */}
      </div>
      <div className="lg:col-span-7 p-5 sm:p-6">
        <ul className="divide-y divide-[#1F1F1F]">
          {DESSERTS.items
            .filter((d) => d.available !== false)
            .map((it) => (
              <ItemRow
                key={it.id}
                id={it.id}
                name={it.name}
                price={it.price}
                addable
                testIdPrefix="menu-add-dessert"
              />
            ))}
        </ul>
        {DESSERTS.note && (
          <div className="mt-3 text-[11px] text-[#A1A1AA] italic">
            {DESSERTS.note}
          </div>
        )}
      </div>
    </article>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Offres                                                                     */
/* -------------------------------------------------------------------------- */

const OffersSection = () => {
  const formatAvail = (o) => {
    if (!o.active_days || o.active_days.length === 0) return null;
    const sorted = [...o.active_days].sort((a, b) => a - b);
    const isRange = sorted.every((v, i, arr) => i === 0 || v === arr[i - 1] + 1);
    const full = ["", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
    return isRange && sorted.length >= 2
      ? `Du ${full[sorted[0]]} au ${full[sorted[sorted.length - 1]]}`
      : sorted.map((d) => full[d]).join(" · ");
  };

  return (
    <section id={OFFERS.anchor} className="scroll-mt-24 py-10">
      <SectionTitle kicker="Bons plans" title={OFFERS.title} icon={Tag} />
      <div className="grid gap-5 sm:grid-cols-2">
        {OFFERS.items
          .filter((o) => o.active && o.show_on_offers_page !== false)
          .map((o) => {
            const days = formatAvail(o);
            const hours =
              o.start_time && o.end_time
                ? `${o.start_time.replace(":", "h")} – ${o.end_time.replace(":", "h")}`
                : null;
            return (
              <article
                key={o.id}
                data-testid={`menu-offer-${o.id}`}
                className="relative overflow-hidden rounded-2xl border border-[#FF9500]/30 bg-gradient-to-br from-[#FF9500]/10 to-transparent p-5 sm:p-6"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-street text-xl sm:text-2xl tracking-wide">
                      {o.title}
                    </div>
                    {o.subtitle && (
                      <div className="mt-0.5 text-[10px] uppercase tracking-[0.22em] text-[#FFC447] font-street">
                        {o.subtitle}
                      </div>
                    )}
                    {(days || hours) && (
                      <div className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#FFC447] font-street">
                        <Clock3 size={12} />
                        {[days, hours].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="font-display text-3xl text-[#FF9500] leading-none whitespace-nowrap">
                    {o.price}
                  </div>
                </div>

                <ul className="mt-4 space-y-1.5">
                  {(o.composition || []).map((c) => (
                    <li
                      key={c}
                      className="text-sm text-white flex items-start gap-2 leading-snug"
                    >
                      <CheckCircle2 size={13} className="text-[#FF9500] mt-1 shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>

                {o.drink_hint && (
                  <div className="mt-3 text-[11px] italic text-white/55 leading-snug">
                    {o.drink_hint}
                  </div>
                )}

                {o.conditions && (
                  <div className="mt-2 text-[10px] italic text-white/40 leading-snug">
                    {o.conditions}
                  </div>
                )}
              </article>
            );
          })}
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*  Menu — page                                                                */
/* -------------------------------------------------------------------------- */

const navAnchors = [
  { anchor: "pizza", label: "Pizza" },
  { anchor: "menus-offres", label: "Menus & Offres" },
  { anchor: "starters", label: "Starters" },
  { anchor: "boissons-desserts", label: "Boissons & Desserts" },
];

/* -------------------------------------------------------------------------- */
/*  Sous-navigation "Menus & Offres" — onglets internes                       */
/* -------------------------------------------------------------------------- */

const TABS = [
  { id: "offres", label: "Offres", anchor: OFFERS.anchor || "offres" },
  { id: "burgers", label: "Burgers", anchor: BURGERS.anchor },
  { id: "naan", label: "Naan", anchor: NAAN.anchor },
  { id: "merguez", label: "Merguez", anchor: MERGUEZ.anchor },
  {
    id: "accompagnements",
    label: "Accompagnements",
    anchor: SIDES.anchor,
  },
];

const tabFromHash = (hash) => {
  if (!hash) return null;
  const h = hash.replace("#", "").toLowerCase();
  const match = TABS.find((t) => t.id === h || t.anchor === h);
  return match ? match.id : null;
};

const MenusOffresTabs = ({ initialHash }) => {
  // Offres en premier, sélectionné par défaut.
  const [tab, setTab] = useState(() => tabFromHash(initialHash) || "offres");

  // Si l'utilisateur arrive par /menu#burgers, /menu#offres, etc., basculer.
  useEffect(() => {
    const next = tabFromHash(initialHash);
    if (next && next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHash]);

  return (
    <section
      data-testid="menus-offres-tabs"
      aria-label="Menus & Offres — sous-navigation"
      className="-mt-2"
    >
      {/* Onglets */}
      <div
        role="tablist"
        aria-label="Catégories Menus & Offres"
        className="flex gap-1.5 overflow-x-auto py-2 -mx-1 px-1 scrollbar-thin"
      >
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`menus-offres-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`shrink-0 inline-flex items-center rounded-full px-3.5 py-1.5 text-[11px] sm:text-[12px] uppercase tracking-[0.18em] sm:tracking-[0.22em] font-street whitespace-nowrap transition-all ${
                active
                  ? "bg-[#F5EFE6] text-[#0A0A0A] shadow-[0_3px_0_rgba(0,0,0,0.4)]"
                  : "border border-[#262626] bg-[#0A0A0A] text-white/65 hover:text-[#F5EFE6] hover:border-[#F5EFE6]/25"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Panneaux — un seul rendu à la fois pour économiser le DOM et garder
          les data-testid uniques. Les ids internes (#burgers, etc.) restent
          présents sur la section visible, donc les deep links continuent
          de fonctionner. */}
      <div role="tabpanel" className="mt-2">
        {tab === "offres" && <OffersSection />}
        {tab === "burgers" && (
          <ProductBlock
            anchor={BURGERS.anchor}
            kicker="Faits maison"
            group={BURGERS}
            icon={Beef}
            withMenuOption
          />
        )}
        {tab === "naan" && (
          <ProductBlock
            anchor={NAAN.anchor}
            kicker="Pain fromage"
            group={NAAN}
            icon={Sandwich}
            withMenuOption
          />
        )}
        {tab === "merguez" && (
          <ProductBlock
            anchor={MERGUEZ.anchor}
            kicker="Le classique"
            group={MERGUEZ}
            icon={Flame}
            withMenuOption
          />
        )}
        {tab === "accompagnements" && <SidesSection />}
      </div>
    </section>
  );
};

const Menu = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        // Petit délai pour laisser le DOM rendre les sections
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div
      data-testid={MENU.root}
      className="mx-auto max-w-6xl px-4 sm:px-6 pt-10 sm:pt-14 pb-24 bg-[#0A0A0A] text-white"
    >
      <Seo
        title="Menu LE 11 - Pizza, burgers paratha, cheese naan et sandwich merguez"
        description="La carte complète LE 11 à Villiers-le-Bel : pizza personnalisée, burgers paratha, cheese naan, sandwich merguez, starters, accompagnements, boissons et desserts."
      />
      {/* En-tête */}
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.28em] text-[#C9A875] font-street">
          La carte complète
        </div>
        <h1 className="font-street mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-wide leading-[0.95]">
          Goût • Qualité • <span className="text-[#C9A875]">Passion</span>
        </h1>
        <div className="mt-5 flex flex-wrap gap-3 items-center">
          <a
            href={RESTAURANT.phoneTel}
            className="font-street inline-flex items-center gap-2 rounded-md border border-[#F5EFE6]/20 bg-[#F5EFE6]/[0.04] px-5 py-3 text-sm tracking-wider text-[#F5EFE6] hover:border-[#F5EFE6]/40 hover:bg-[#F5EFE6]/[0.08] transition-all"
          >
            <Phone size={16} strokeWidth={2.6} className="text-[#C9A875]" />
            {RESTAURANT.phoneDisplay}
          </a>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#C9A875]/25 bg-[#C9A875]/[0.06] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#C9A875] font-street">
            <ShieldCheck size={12} />
            {COPY.halalBadge}
          </span>
          <a
            href="/allergenes"
            data-testid="menu-allergens-link"
            className="font-street inline-flex items-center gap-1.5 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 py-2 text-[11px] tracking-wider text-white/70 hover:text-[#F5EFE6] hover:border-[#F5EFE6]/25 transition-colors"
          >
            Consulter la liste des allergènes
          </a>
        </div>
      </div>

      {/* Sticky anchor nav — 4 grandes catégories */}
      <nav
        aria-label="Catégories"
        className="mt-8 -mx-4 sm:mx-0 sm:rounded-xl sticky top-16 z-20 border-y sm:border border-[#262626] bg-[#0A0A0A]/95 backdrop-blur"
      >
        <ul className="grid grid-cols-4 gap-1 px-2 py-2 sm:py-2.5">
          {navAnchors.map((a) => (
            <li key={a.anchor} className="min-w-0">
              <a
                href={`#${a.anchor}`}
                data-testid={`menu-anchor-${a.anchor}`}
                className="block w-full text-center rounded-md px-1.5 sm:px-3 py-1.5 text-[10.5px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.22em] font-street text-white/70 hover:text-[#F5EFE6] hover:bg-[#F5EFE6]/[0.04] transition-colors leading-tight"
              >
                {a.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* ── 1. Pizza ──────────────────────────────────────────── */}
      <PizzaSignaturesSection />

      {/* ── 2. Menus & Offres ────────────────────────────────── */}
      <GroupHeader
        id="menus-offres"
        kicker="Faits maison"
        title="Menus & Offres"
        intro="Bons plans du moment, burgers paratha, naan, sandwich merguez, accompagnements."
      />
      <MenusOffresTabs initialHash={hash} />

      {/* ── 3. Starters ──────────────────────────────────────── */}
      <GroupHeader
        id="starters"
        kicker="Pour commencer"
        title="Starters"
        intro="Cuit au tandoor · Nems & Samoussas."
      />
      <EntreesSection />

      {/* ── 4. Boissons & Desserts ───────────────────────────── */}
      <GroupHeader
        id="boissons-desserts"
        kicker="À boire · Sucré"
        title="Boissons & Desserts"
        intro="Sodas, eau, lassi mangue, donuts, cookies, brownies, tiramisu."
      />
      <DrinksSection />
      <DessertsSection />
    </div>
  );
};

export default Menu;
